/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import { call, put, select, takeLatest, take } from 'redux-saga/effects';

import { selectSettingsLoaded } from '@nebula/airglow-plugin-unified-shell';
import { INITED_SETTINGS } from '@nebula/airglow-plugin-unified-shell/dist/actions';
import PluginEndpoint from '@nebula/dorado-plugin-endpoint';
import ValidationEndpoint from '@nebula/dorado-validation-endpoint';

import { expiringSessions } from '../../casing/data/feature.flags';
import { featureFlagsLoaded } from '../../casing/data/prefabs';
import { LOADED_FEATURE_FLAGS, writeRecentSessionsToUser } from '../../common/actions';
import { selectInitedStateful } from '../../common/sagas';
import {
  selectActiveSession,
  selectRecentSessions,
  selectSessionRouteParam
} from '../../common/selectors';
import { isSessionExpiring } from '../../common/util/session.utils';
import { sessionExpiring } from '../../ui/toast.list';
import * as T from '../actions';
import enablePlugins from './enable.plugins.saga';
import { pluginEndpoint, sessionNotFound, validationEndpoint } from '../data/prefabs';
import {
  getSelectedViewNamespace,
  selectValidationPluginSettings,
  selectViewInitSettings
} from '../selectors';

export function* connectSession(stateful, sessionUuid) {
  yield stateful.autoLoadSessionsPromise;
  const sessionFound = yield call([stateful, 'connectSession'], { uuid: sessionUuid });

  if (!sessionFound) {
    yield put(sessionNotFound.setAction);
    return false;
  }

  const session = yield select(selectActiveSession);
  const nowTs = Date.now();
  const createdTsDate = new Date(session.createdTs);
  const showExpiringSessions = yield select(expiringSessions);

  if (showExpiringSessions && isSessionExpiring(nowTs, createdTsDate.getTime())) {
    yield put(sessionExpiring);
  }

  return sessionFound;
}

export function* registerEndpoints(stateful) {
  yield stateful.autoLoadPluginsPromise;
  yield call(enablePlugins);

  const settings = yield select(selectValidationPluginSettings);
  const vEndpoint = new ValidationEndpoint({ env: settings.env, settings });
  yield call([stateful, 'registerValidationEndpoint'], vEndpoint);
  yield put(validationEndpoint.changeAction(vEndpoint));

  const featureFlagsLoadedSet = yield select(featureFlagsLoaded.isSet);
  if (!featureFlagsLoadedSet) {
    yield take(LOADED_FEATURE_FLAGS);
  }

  const viewInitSettings = yield select(selectViewInitSettings);
  const { env, ...configuration } = viewInitSettings;

  const pEndpoint = new PluginEndpoint({
    env,
    configuration
  });
  yield call([stateful, 'registerPluginEndpoint'], pEndpoint);
  yield put(pluginEndpoint.changeAction(pEndpoint));

  const view = yield select(getSelectedViewNamespace);

  yield put(T.selectView(view));
}

export function* handleModuleLoaded() {
  if (window['exc-module-runtime']) {
    window['exc-module-runtime'].sidebar.collapsed = true;
  }

  const settingsInited = yield select(selectSettingsLoaded);

  if (!settingsInited) {
    yield take(INITED_SETTINGS);
  }

  const sessionUuid = yield select(selectSessionRouteParam);
  const recentSessions = yield select(selectRecentSessions);
  if (recentSessions.indexOf(sessionUuid) === -1) {
    const updatedRecentSessions = R.pipe(
      R.prepend(sessionUuid),
      R.slice(0, 24),
    )(recentSessions);
    yield put(writeRecentSessionsToUser(updatedRecentSessions));
  }

  const stateful = yield call(selectInitedStateful);

  const connectedSession = yield call(connectSession, stateful, sessionUuid);
  if (connectedSession) {
    yield call(registerEndpoints, stateful);
  }
}

export default function* watchModuleLoaded() {
  yield takeLatest(T.MODULE_LOADED, handleModuleLoaded);
}
